import {
	ExtendedFocusarea,
	ExtendedOutcome,
	useOutcomeContext,
} from '@/components/outcomes/KontentOutcomeWrapper'
import { Outcome } from '@/kontent/content-types'
import { isYes } from '@/utils'
import Icon from '@/components/Icon'
import Link from 'next/link'
import { useState } from 'react'
import { OutcomePaperOverarchingOutcomeWarning } from './OutcomePaperOverarchingOutcomeWarning'

export interface OutcomePaperAlignedContentProps {
	outcome: Outcome | ExtendedOutcome
}

interface AlignedFocusAreaGroup {
	id: string
	title: string
	items: ExtendedFocusarea[]
}

/**
 * Groups aligned focus areas by their parent options list.
 * Items without a parent options list become their own group (self-grouped).
 */
const groupAlignedFocusAreas = (
	alignedFocusAreas: ExtendedFocusarea[],
): AlignedFocusAreaGroup[] => {
	const groups: Record<string, AlignedFocusAreaGroup> = {}

	alignedFocusAreas.forEach((item) => {
		if (item.optionList) {
			// Has parent options list - group under it
			const groupKey = item.optionList.system.codename
			if (!groups[groupKey]) {
				groups[groupKey] = {
					id: groupKey,
					title: item.optionList.elements.title.value,
					items: [],
				}
			}
			groups[groupKey].items.push(item)
		} else {
			// No parent - self-grouped
			const groupKey = item.system.codename
			groups[groupKey] = {
				id: groupKey,
				title: item.elements.title.value,
				items: [item],
			}
		}
	})

	return Object.values(groups)
}

export const OutcomePaperAlignedContent = ({
	outcome,
}: OutcomePaperAlignedContentProps) => {
	const { syllabus } = useOutcomeContext()

	const alignedFocusAreas =
		'alignedFocusAreas' in outcome ? outcome.alignedFocusAreas : undefined

	const isOverarching = isYes(outcome.elements.isoverarching)

	// Check if collapsing is enabled from syllabus configuration
	const shouldCollapseOptionsListContent = isYes(
		syllabus?.elements.collapse_optionslist_alignedcontent,
	)

	// State for tracking which groups are expanded
	const [expandedGroups, setExpandedGroups] = useState<
		Record<string, boolean>
	>({})

	// Toggle group expand/collapse
	const toggleGroup = (groupId: string) => {
		setExpandedGroups((prev) => ({
			...prev,
			[groupId]: !prev[groupId],
		}))
	}

	// if alignedFocusAreas is empty
	if (!alignedFocusAreas?.length && !isOverarching) return null

	// Group the aligned focus areas (only used when collapse is enabled)
	const groups = alignedFocusAreas
		? groupAlignedFocusAreas(alignedFocusAreas)
		: []

	return (
		<div className="mt-3 pt-3 border-t">
			<p className="mb-0 text-subtext">
				{isOverarching ? (
					<OutcomePaperOverarchingOutcomeWarning />
				) : (
					'Content aligned to outcome'
				)}
			</p>
			{!isOverarching && (
				<ul className="nsw-small list-disc pl-[18px] mt-0 space-y-0">
					{shouldCollapseOptionsListContent
						? // Collapsed/Grouped view - group by parent options list
						  groups.map((group) => {
								const isExpanded =
									expandedGroups[group.id] || false
								const isSelfGrouped =
									group.items.length === 1 &&
									!group.items[0].optionList

								// Self-grouped items (no parent optionList) - render as regular list item
								if (isSelfGrouped) {
									const item = group.items[0]
									return (
										<li
											key={group.id}
											data-item="Focus Area"
										>
											<Link
												href={item.contentTabUrl}
												scroll={false}
											>
												<a>{group.title}</a>
											</Link>
										</li>
									)
								}

								// Grouped items - render as collapsible list item
								return (
									<li key={group.id} data-item="Option List">
										<button
											type="button"
											onClick={() =>
												toggleGroup(group.id)
											}
											className="inline-flex items-center justify-between w-full text-left"
											aria-expanded={isExpanded}
										>
											<span className="text-nsw-brand-dark underline font-bold">
												{group.title}
											</span>
											<Icon
												icon="ic:baseline-keyboard-arrow-right"
												width={18}
												height={18}
												className="ml-1 transition-transform duration-200"
												style={{
													transform: isExpanded
														? 'rotate(90deg)'
														: 'rotate(0deg)',
													flexShrink: 0,
												}}
											/>
										</button>
										{isExpanded && (
											<ul className="nsw-small list-[circle] pl-[18px] mt-[-8px] space-y-0">
												{group.items.map((item) => (
													<li
														key={item.system.id}
														data-item="Focus Area Option"
													>
														<Link
															href={
																item.contentTabUrl
															}
															scroll={false}
														>
															<a>
																{
																	item
																		.elements
																		.title
																		.value
																}
															</a>
														</Link>
													</li>
												))}
											</ul>
										)}
									</li>
								)
						  })
						: // Flat view - display all focus areas without grouping
						  alignedFocusAreas.map((item) => (
								<li key={item.system.id} data-item="Focus Area">
									<Link
										href={item.contentTabUrl}
										scroll={false}
									>
										<a>{item.elements.title.value}</a>
									</Link>
								</li>
						  ))}
				</ul>
			)}
		</div>
	)
}
